import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, DollarSign } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { useInventory } from '../context/InventoryContext';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import Badge from '../components/ui/Badge';

interface OrderItem {
  product_id: string;
  quantity: number;
  unit_price: number;
}

const PurchaseOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { products } = useInventory();
  const isNewOrder = id === 'new';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orderNumber, setOrderNumber] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [suppliers, setSuppliers] = useState<{ id: string; name: string }[]>([]);
  const [expectedDate, setExpectedDate] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<OrderItem[]>([]);
  const [orderStatus, setOrderStatus] = useState('draft');
  const [paymentStatus, setPaymentStatus] = useState('pending');

  useEffect(() => {
    loadSuppliers();
    if (!isNewOrder) {
      loadPurchaseOrder();
    } else {
      generateOrderNumber();
    }
  }, [id]);

  const loadSuppliers = async () => {
    try {
      const { data, error } = await supabase
        .from('suppliers')
        .select('id, name')
        .eq('status', 'active');

      if (error) throw error;
      setSuppliers(data || []);
    } catch (error) {
      console.error('Error loading suppliers:', error);
      setError('Failed to load suppliers');
    }
  };

  const generateOrderNumber = () => {
    const prefix = 'PO';
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    setOrderNumber(`${prefix}-${timestamp}-${random}`);
  };

  const loadPurchaseOrder = async () => {
    try {
      const { data: order, error } = await supabase
        .from('purchase_orders')
        .select(`
          *,
          purchase_order_items (
            id,
            product_id,
            quantity,
            unit_price
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      if (order) {
        setOrderNumber(order.order_number);
        setSupplierId(order.supplier_id || '');
        setExpectedDate(order.expected_date ? new Date(order.expected_date).toISOString().split('T')[0] : '');
        setNotes(order.notes || '');
        setItems(order.purchase_order_items || []);
        setOrderStatus(order.status);
        setPaymentStatus(order.payment_status);
      }
    } catch (error) {
      console.error('Error loading purchase order:', error);
      setError('Failed to load purchase order');
    }
  };

  const handleAddItem = () => {
    setItems([...items, { product_id: '', quantity: 1, unit_price: 0 }]);
  };

  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index: number, field: keyof OrderItem, value: string | number) => {
    const newItems = [...items];
    if (field === 'product_id' && typeof value === 'string') {
      const product = products.find(p => p.id === value);
      newItems[index] = {
        ...newItems[index],
        [field]: value,
        unit_price: product?.cost_price || 0
      };
    } else {
      newItems[index] = {
        ...newItems[index],
        [field]: value
      };
    }
    setItems(newItems);
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const validateForm = () => {
    if (!supplierId) {
      setError('Please select a supplier');
      return false;
    }
    if (items.length === 0) {
      setError('Please add at least one item');
      return false;
    }
    if (items.some(item => !item.product_id)) {
      setError('Please select products for all items');
      return false;
    }
    if (items.some(item => item.quantity <= 0)) {
      setError('Quantity must be greater than 0 for all items');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const orderData = {
        user_id: user?.id,
        supplier_id: supplierId,
        order_number: orderNumber,
        expected_date: expectedDate || null,
        notes: notes || null,
        total_amount: calculateTotal(),
        status: orderStatus,
        payment_status: paymentStatus,
        currency: 'USD'
      };

      if (isNewOrder) {
        const { data: order, error: orderError } = await supabase
          .from('purchase_orders')
          .insert([orderData])
          .select()
          .single();

        if (orderError) throw orderError;

        const { error: itemsError } = await supabase
          .from('purchase_order_items')
          .insert(
            items.map(item => ({
              purchase_order_id: order.id,
              product_id: item.product_id,
              quantity: item.quantity,
              unit_price: item.unit_price,
              status: 'pending'
            }))
          );

        if (itemsError) throw itemsError;
      } else {
        const { error: orderError } = await supabase
          .from('purchase_orders')
          .update(orderData)
          .eq('id', id);

        if (orderError) throw orderError;

        const { error: deleteError } = await supabase
          .from('purchase_order_items')
          .delete()
          .eq('purchase_order_id', id);

        if (deleteError) throw deleteError;

        const { error: itemsError } = await supabase
          .from('purchase_order_items')
          .insert(
            items.map(item => ({
              purchase_order_id: id,
              product_id: item.product_id,
              quantity: item.quantity,
              unit_price: item.unit_price,
              status: 'pending'
            }))
          );

        if (itemsError) throw itemsError;
      }

      navigate('/purchase-orders');
    } catch (error) {
      console.error('Error saving purchase order:', error);
      setError('Failed to save purchase order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      setLoading(true);
      setError(null);

      const { error } = await supabase
        .from('purchase_orders')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) throw error;

      setOrderStatus(newStatus);
    } catch (error) {
      console.error('Error updating status:', error);
      setError('Failed to update order status');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    try {
      setLoading(true);
      setError(null);

      const { error } = await supabase
        .from('purchase_orders')
        .update({ 
          payment_status: 'paid',
          status: orderStatus === 'draft' ? 'ordered' : orderStatus
        })
        .eq('id', id);

      if (error) throw error;

      setPaymentStatus('paid');
      if (orderStatus === 'draft') {
        setOrderStatus('ordered');
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      setError('Failed to process payment');
    } finally {
      setLoading(false);
    }
  };

  const supplierOptions = [
    { value: '', label: 'Select' },
    ...suppliers.map(supplier => ({
      value: supplier.id,
      label: supplier.name
    }))
  ];

  const productOptions = [
    { value: '', label: 'Select' },
    ...products.map(product => ({
      value: product.id,
      label: `${product.name} (${product.sku}) - $${product.cost_price}`
    }))
  ];

  const canCompletePayment = orderStatus === 'draft' && paymentStatus === 'pending';
  const canSubmitOrder = !isNewOrder && orderStatus === 'draft' && paymentStatus === 'paid';

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/purchase-orders')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isNewOrder ? 'Create Purchase Order' : 'Edit Purchase Order'}
        </h1>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-md">
          {error}
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Order Information</CardTitle>
            {!isNewOrder && (
              <div className="flex items-center gap-2">
                <Badge variant={orderStatus === 'draft' ? 'secondary' : 'primary'}>
                  {orderStatus}
                </Badge>
                <Badge variant={paymentStatus === 'paid' ? 'success' : 'warning'}>
                  {paymentStatus}
                </Badge>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Order Number"
              value={orderNumber}
              disabled
              fullWidth
            />
            <Select
              label="Supplier"
              options={supplierOptions}
              value={supplierId}
              onChange={(value) => setSupplierId(value)}
              error={!supplierId ? 'Please select a supplier' : undefined}
              fullWidth
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Expected Delivery Date"
              type="date"
              value={expectedDate}
              onChange={(e) => setExpectedDate(e.target.value)}
              fullWidth
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Order Items</CardTitle>
            <Button onClick={handleAddItem}>
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {items.map((item, index) => (
              <div key={index} className="grid grid-cols-12 gap-4 items-end">
                <div className="col-span-5">
                  <Select
                    label="Product"
                    options={productOptions}
                    value={item.product_id}
                    onChange={(value) => handleItemChange(index, 'product_id', value)}
                    fullWidth
                    required
                  />
                </div>
                <div className="col-span-2 mr-2">
                  <Input
                    label="Quantity"
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 0)}
                    required
                    fullWidth
                  />
                </div>
                <div className="col-span-3 mr-2">
                  <Input
                    label="Unit Price"
                    type="number"
                    step="0.01"
                    value={item.unit_price}
                    onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                    required
                    fullWidth
                  />
                </div>
                <div className="col-span-2 flex justify-center items-end pb-2">
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleRemoveItem(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}

            {items.length === 0 && (
              <div className="text-center py-4 text-gray-500">
                No items added yet. Click "Add Item" to start adding products.
              </div>
            )}

            {items.length > 0 && (
              <div className="flex justify-end pt-4 border-t">
                <div className="text-right">
                  <span className="text-sm text-gray-500">Total:</span>
                  <span className="ml-2 text-lg font-bold">${calculateTotal().toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button
            variant="secondary"
            onClick={() => navigate('/purchase-orders')}
          >
            Cancel
          </Button>
          <div className="flex gap-2">
            {canCompletePayment && (
              <Button
                onClick={handlePayment}
                disabled={loading}
              >
                <DollarSign className="h-4 w-4 mr-2" />
                Complete Payment
              </Button>
            )}
            {canSubmitOrder && (
              <Button
                onClick={() => handleStatusChange('ordered')}
                disabled={loading}
              >
                Submit Order
              </Button>
            )}
            <Button
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Saving...' : (isNewOrder ? 'Create Order' : 'Update Order')}
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default PurchaseOrderDetailPage;